import { Fragment, type ReactNode } from 'react';

// The Beverly Wilshire name is authored with an inline size override for
// "A Four Seasons Hotel" wherever it appears. Different CMS/backend pipelines
// author this differently — see formatBeverlyWilshire in @fshr/shared and
// HandleContent.formatBeverlyWilshire in @fshr/slingql, which both convert
// plain text into this span but disagree on quote style and on the exact
// style value (".8em" vs "font-size: .8em;" vs sanitize-html's normalized
// "font-size:.8em"). Rather than hardcode one of those, match the plain text
// or a markup span with ANY quote/style value, and always emit the same
// canonical real span so the result renders identically regardless of which
// upstream form arrives. Everywhere else, children pass through untouched.
const SPAN_TEXT = 'A Four Seasons Hotel';
// The lookbehind keeps "Beverly Wilshire, " out of the match itself, so split()
// leaves it attached to the preceding text chunk instead of discarding it —
// only the "A Four Seasons Hotel" portion is replaced with the real span.
const MATCH_RE = new RegExp(
	`(?<=Beverly Wilshire, )(?:<span style=(?:"[^"]*"|'[^']*')>${SPAN_TEXT}</span>|${SPAN_TEXT})`
);

export type Props = {
	children: ReactNode;
};

export default function BevSafeText({ children }: Props): ReactNode {
	if (typeof children !== 'string') {
		return children;
	}

	const parts = children.split(MATCH_RE);
	if (parts.length === 1) {
		return children;
	}

	const nodes: ReactNode[] = [];
	parts.forEach((part, index) => {
		nodes.push(part);
		if (index < parts.length - 1) {
			nodes.push(<span style={{ fontSize: '.8em' }}>{SPAN_TEXT}</span>);
		}
	});

	return (
		<>
			{nodes.map((node, index) => (
				<Fragment key={index}>{node}</Fragment>
			))}
		</>
	);
}
